import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { Badge, Tabs } from '@/kit/ui'
import { useHubStore } from '@/store/hubStore'
import { ActiveSwitch, LenderLogo, StatusPill } from '../lenders/LenderBits'
import { ApiTab } from './api/ApiTab'
import { HistoryTab } from './HistoryTab'
import { InfoTab } from './InfoTab'
import { ProductsTab } from './ProductsTab'
import { EnvTab } from './EnvTab'
import { InAppTab } from './InAppTab'

/** Tab 6 · In the app is hidden (HR1, owner 2026-10-28): back when the offer-check journey is built. Its code, API and tests stay. */
export const SHOW_IN_APP_TAB = false

type TabId = 'info' | 'products' | 'api' | 'history' | 'env' | 'app'
const TABS: { id: TabId; label: string }[] = [
  { id: 'info', label: '1 · Lender info' },
  { id: 'products', label: '2 · Products & key facts' },
  { id: 'api', label: '3 · Lender API workflow' },
  { id: 'history', label: '4 · History' },
  { id: 'env', label: '5 · Env variables' },
  ...(SHOW_IN_APP_TAB ? [{ id: 'app' as const, label: '6 · In the app' }] : []),
]

/** One lender: header with the single switch, the four tabs the owner asked for plus 5 · Env variables (LH-F5) and 6 · In the app (R-1, the old admin's tab); the tab is in the address. */
export function LenderScreen() {
  const { slug = '', tab = 'info', sub } = useParams()
  const navigate = useNavigate()
  const lender = useHubStore((s) => s.lenders.find((l) => l.slug === slug))
  if (!lender) return <Navigate to="/lenders" replace />
  // an old /lenders/:slug/app address opens tab 1 while tab 6 is hidden
  if (tab === 'app' && !SHOW_IN_APP_TAB) return <Navigate to={`/lenders/${slug}/info`} replace />
  const active = (TABS.some((t) => t.id === tab) ? tab : 'info') as TabId
  const liveEndpoints = lender.workflows.filter((w) => w.state === 'live').length

  return (
    <>
      <div className="mb-1 text-2xs text-muted"><Link to="/lenders">Lenders</Link> <span aria-hidden="true">›</span> {lender.name}</div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <LenderLogo lender={lender} />
        <div className="min-w-0">
          <h1 className="page-title">{lender.name}</h1>
          <div className="page-subtitle">
            {lender.legalName} · <code>{lender.slug}</code> · {lender.products.length} product{lender.products.length === 1 ? '' : 's'}
            {lender.liveBatch ? <> · live batch {lender.liveBatch}</> : null} · {liveEndpoints} live endpoint{liveEndpoints === 1 ? '' : 's'}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <StatusPill lender={lender} />
          {lender.status === 'draft' ? <Link to="/lenders/new" state={{ slug: lender.slug }} className="btn btn-primary no-underline">Finish onboarding</Link> : <ActiveSwitch lender={lender} asButton />}
        </div>
      </div>
      {lender.status === 'off' && (
        <div className="mb-3 rounded border-l-[3px] border-red-500 bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-200">
          <Badge tone="danger" className="mr-2">Off</Badge>Hidden on the website and in the app; endpoints suspended. Reason: “{lender.offReason}”.
        </div>
      )}
      <Tabs tabs={TABS} value={active} onChange={(t) => navigate(`/lenders/${lender.slug}/${t}`)} ariaLabel="Lender sections" panelId="lender" />
      <div className="pt-4">
        {active === 'info' && <InfoTab lender={lender} />}
        {active === 'products' && <ProductsTab lender={lender} />}
        {active === 'api' && <ApiTab lender={lender} sub={sub} />}
        {active === 'history' && <HistoryTab lender={lender} />}
        {active === 'env' && <EnvTab lender={lender} />}
        {SHOW_IN_APP_TAB && active === 'app' && <InAppTab lender={lender} />}
      </div>
    </>
  )
}
